// CipModal.jsx
import { useState, useRef, useCallback, useEffect, Fragment, useContext } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { ProductsContext } from '../context/products.context'
import CipSnippet from '../pages/cips/CipSnippet'
import CipSkeleton from './CipSkeleton'
import { CIP_NAMES } from '../data/CIP_NAMES'
import FloatingTooltip from './FloatingTooltip'
import { throttle } from 'lodash'

export default function CipModal({ cipCode, offeredCip }) {
  const { currentCip, cipDoc, setCurrentCip, clearCipData } = useContext(ProductsContext)
  const [isOpen, setIsOpen] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const tooltipCoords = useRef({ x: null, y: null })

  // Format the CIP code (ex. 12345678 -> 1234.5678)
  const formattedCip = cipCode.toString().replace(/\B(?=(\d{4})+(?!\d))/g, '.')
  // Look up the CIP name (with fallback)
  const tooltipContent = CIP_NAMES[cipCode] || 'CIP Name Not Found'

  function openModal() {
    if (currentCip === cipCode && cipDoc?.cipDetails) {
      setIsOpen(true)
      return
    }
    clearCipData()
    setCurrentCip(cipCode)
    setIsOpen(true)
  }

  function closeModal() {
    setIsOpen(false)
  }

  const handleMouseEnter = () => setTooltipOpen(true)
  const throttledMouseMove = useCallback(
    throttle((event) => {
      tooltipCoords.current = { x: event.clientX, y: event.clientY }
    }, 10),
    []
  )
  const handleMouseMove = (event) => throttledMouseMove(event)
  const handleMouseLeave = () => setTooltipOpen(false)

  useEffect(() => {
    return () => {
      throttledMouseMove.cancel()
    }
  }, [throttledMouseMove])

  const isLoaded = cipDoc?.cipDetails

  return (
    <div>
      <button
        onClick={openModal}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className={`${
          offeredCip?.includes(cipCode)
            ? 'bg-sky-100 text-sky-700 hover:border-sky-200'
            : 'bg-gray-50 text-gray-500 hover:border-gray-200'
        } rounded-md p-1 m-1 text-xs border-2 border-transparent tabular-nums`}
      >
        {formattedCip}
      </button>

      <FloatingTooltip
        open={tooltipOpen}
        coordsRef={tooltipCoords}
        text={tooltipContent}
        color="#333"
      />

      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40" onClose={setIsOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden my-[100px] rounded-lg bg-white text-left shadow-xl transition-all w-full max-w-[900px]">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="mt-4">
                      {isLoaded ? <CipSnippet /> : <CipSkeleton />}
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto"
                      onClick={closeModal}
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  )
}